import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import CadastroDataTable from '../components/CadastroDataTable/CadastroDataTable';
import api, { normalize } from '../services/api';
import './nfse/NfseConsulta.css';

const EMPTY_CONTA = {
  banco: '',
  agencia: '',
  conta: '',
  convenio: '',
  provider: 'GenericoCnab',
  ambienteApi: 'Homologacao',
};

const PROVIDERS = [
  'Itau', 'Inter', 'Cora', 'MercadoPago', 'Sicredi', 'Bradesco', 'Caixa',
  'Santander', 'BTG', 'Sicoob', 'C6', 'Nubank', 'GenericoCnab',
].map((p) => ({ label: p, value: p }));

export default function ContasBancariasPanel({ embedded = false }) {
  const toast = useRef(null);
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contaVisible, setContaVisible] = useState(false);
  const [formConta, setFormConta] = useState(EMPTY_CONTA);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/financeiro/contas-bancarias');
      setContas(normalize(data));
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: e.response?.data?.message || 'Falha ao carregar contas.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const criarConta = async () => {
    if (!formConta.banco?.trim() || !formConta.agencia?.trim() || !formConta.conta?.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Banco, agência e conta são obrigatórios.' });
      return;
    }
    setSalvando(true);
    try {
      await api.post('/financeiro/contas-bancarias', formConta);
      toast.current?.show({ severity: 'success', summary: 'Conta', detail: 'Conta bancária criada.' });
      setContaVisible(false);
      setFormConta(EMPTY_CONTA);
      carregar();
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: e.response?.data?.message || 'Falha ao criar conta.',
      });
    } finally {
      setSalvando(false);
    }
  };

  const contaCols = [
    { field: 'banco', header: 'Banco' },
    { field: 'agencia', header: 'Agência', style: { width: 100 } },
    { field: 'conta', header: 'Conta', style: { width: 120 } },
    { field: 'provider', header: 'Provider', style: { width: 140 } },
    { field: 'ambienteApi', header: 'Ambiente', style: { width: 120 } },
  ];

  return (
    <div className={embedded ? '' : 'nfse-consulta-page'}>
      <Toast ref={toast} />
      {!embedded && (
        <div className="nfse-consulta-header">
          <h1>Contas bancárias</h1>
        </div>
      )}
      <div className="nfse-consulta-header-actions" style={{ marginBottom: 12 }}>
        <Button
          label="Nova conta"
          icon="pi pi-plus"
          onClick={() => {
            setFormConta(EMPTY_CONTA);
            setContaVisible(true);
          }}
        />
      </div>
      <CadastroDataTable
        value={contas}
        columns={contaCols}
        storageKey="fin-contas"
        loading={loading}
        title="Contas bancárias"
        emptyMessage="Nenhuma conta cadastrada."
        globalFilterFields={['banco', 'agencia', 'conta', 'provider']}
      />

      <Dialog
        header="Nova conta bancária"
        visible={contaVisible}
        onHide={() => setContaVisible(false)}
        style={{ width: 480 }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Cancelar" className="p-button-text" onClick={() => setContaVisible(false)} />
            <Button label="Salvar" icon="pi pi-check" loading={salvando} onClick={criarConta} />
          </div>
        }
      >
        <div className="nfse-emissao-grid">
          <div className="nfse-emissao-field">
            <label>Banco</label>
            <InputText value={formConta.banco} onChange={(e) => setFormConta((p) => ({ ...p, banco: e.target.value }))} className="w-full" />
          </div>
          <div className="nfse-emissao-field">
            <label>Agência</label>
            <InputText value={formConta.agencia} onChange={(e) => setFormConta((p) => ({ ...p, agencia: e.target.value }))} className="w-full" />
          </div>
          <div className="nfse-emissao-field">
            <label>Conta</label>
            <InputText value={formConta.conta} onChange={(e) => setFormConta((p) => ({ ...p, conta: e.target.value }))} className="w-full" />
          </div>
          <div className="nfse-emissao-field">
            <label>Convênio</label>
            <InputText value={formConta.convenio} onChange={(e) => setFormConta((p) => ({ ...p, convenio: e.target.value }))} className="w-full" />
          </div>
          <div className="nfse-emissao-field">
            <label>Provider</label>
            <Dropdown value={formConta.provider} options={PROVIDERS} onChange={(e) => setFormConta((p) => ({ ...p, provider: e.value }))} className="w-full" />
          </div>
          <div className="nfse-emissao-field">
            <label>Ambiente API</label>
            <Dropdown
              value={formConta.ambienteApi}
              options={[
                { label: 'Homologação', value: 'Homologacao' },
                { label: 'Produção', value: 'Producao' },
              ]}
              onChange={(e) => setFormConta((p) => ({ ...p, ambienteApi: e.value }))}
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
